import React, { useRef, useState } from 'react';
import { Badge, Label, Icon } from '@nooz-optics/nooz-components-lib';
import { ShopItemVariantImages } from '../VariantImage';
import { ColorPicker } from '../ColorPicker';
import { CardImageHover } from '../../ImageHover';
import { ShopItemQuickBuy } from '../Quickbuy';
import { IShopItem } from '../../../../../utils/types/contentful';
import { ProductPrice } from '../../../Elements/Price/productPrice';
import { withLocale } from '../../../../../utils/hocs';
import { Image } from '../../../Media';
import { useMediaQuery } from '../../../../../utils/hooks/useMediaQuery';
import { Button } from '../../../Elements';
import { Link } from 'components/UI/Elements/Link';
import { Tooltip } from '../../../Overlays';

export const ShopProductItemCard = withLocale(
  ({
    badges,
    button,
    colorVariants,
    compareAtPrice,
    currency,
    hasQuickBuy,
    hasCategoryBadge = true,
    label,
    onClick,
    price,
    product,
    selectedSize,
    selectedVariant,
    setSelectedVariant,
    showButton,
    showColorPicker,
    subLabel,
  }: IShopItem) => {
    const isWindowSmallSize = useMediaQuery('sm');

    const labelSize = isWindowSmallSize ? '2xl' : 'xl';
    const comparePriceLabelSize = isWindowSmallSize ? 'xl' : 'lg';

    const returnCategoryBadges = () =>
      hasCategoryBadge && (
        <div className='absolute right-2 top-2'>
          <div className=' mb-1'>
            {product.categories?.map((cat) => (
              <div className='lg:mb-1'>
                <Badge
                  size='sm'
                  type='simple'
                  dotColor={cat.color}
                  dotIcon={!isWindowSmallSize ? cat.icon : undefined}
                  label={cat.label}
                />
              </div>
            ))}
          </div>
        </div>
      );

    const returnInformationBadges = () => {
      let allBadges = [];

      // Create badge list from various product information
      if (badges && badges.length > 0) {
        allBadges = [...allBadges, ...badges];
      } else if (
        selectedVariant?.promotion &&
        selectedVariant?.promotion.badge
      ) {
        allBadges = [...allBadges, selectedVariant?.promotion.badge];
      }

      return (
        <div className='absolute left-2 top-2'>
          {allBadges?.map((badge) => (
            <div className='mr-2'>
              <Badge
                {...badge}
                dotIcon={!isWindowSmallSize && { iconKey: 'NewMini' }}
                dotPosition='right'
                type='simple'
                size='sm'
              />
            </div>
          ))}
        </div>
      );
    };

    return (
      <div className='w-full h-full flex flex-col cursor-pointer relative'>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <button type='button' className='select-none' onClick={onClick}>
          <Link slug={product.slug} className='hidden' />
          <div className='group flex pointer-events-auto h-full md:h-72 lg:h-80 w-full relative'>
            <ShopItemVariantImages
              product={product}
              variants={colorVariants}
              selectedVariant={selectedVariant}
              selectedSize={selectedSize}
            />
            {returnInformationBadges()}
            {returnCategoryBadges()}
          </div>
        </button>
        {colorVariants && showColorPicker && (
          <div className='w-full transition duration-300 mt-2 sm:mt-4'>
            <ColorPicker
              variants={colorVariants}
              selectedColor={selectedVariant}
              setSelectedColor={setSelectedVariant}
            />
          </div>
        )}
        <div className='flex flex-col w-full truncate overflow-ellipsis justify-between items-start mt-2 sm:mt-4'>
          <div className='flex items-end w-full'>
            <Label
              type='h4'
              weight='bold'
              size={labelSize}
              text={label?.text}
              color={{ classname: 'primary' }}
            />
            &nbsp;
            {selectedVariant?.color?.text && (
              <span className='hidden sm:block'>
                <Label
                  type='h4'
                  weight='light'
                  size={'xl'}
                  text={`- ${selectedVariant?.color?.text || ''}`}
                  color={{ classname: 'gray-700' }}
                  truncate
                  text-ellipsis
                />
              </span>
            )}
          </div>
          <span className='block truncate w-full overflow-ellipsis sm:hidden'>
            <Label
              type='h4'
              weight='light'
              size='xl'
              text={subLabel?.text}
              color={{ classname: 'gray-700' }}
              truncate
            />
          </span>
          {subLabel && (
            <>
              <span className='hidden truncate w-full overflow-ellipsis sm:block'>
                <Label
                  type='h5'
                  weight='light'
                  size={labelSize}
                  text={subLabel.text}
                  color={{ classname: 'gray-700' }}
                  truncate
                  text-ellipsis
                />
              </span>
            </>
          )}

          <span className='mt-1'>
            <ProductPrice
              compareAtPrice={
                compareAtPrice && {
                  text: compareAtPrice,
                  size: comparePriceLabelSize,
                }
              }
              currency={{ text: currency, size: comparePriceLabelSize }}
              price={{ text: price }}
              size={labelSize}
            />
          </span>
        </div>
        {hasQuickBuy && <ShopItemQuickBuy label={{ text: 'quickbuy' }} />}
      </div>
    );
  }
);
